import { api } from './api/client';

/** Push on this device: 'unsupported' (e.g. iPhone outside the home-screen app), 'denied', 'off' or 'on' (doc 28). */
export type PushState = 'unsupported' | 'denied' | 'off' | 'on';

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return (await currentSubscription()) ? 'on' : 'off';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

/** Asks for permission, subscribes this browser and tells the server. */
export async function enablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  if ((await Notification.requestPermission()) !== 'granted') return Notification.permission === 'denied' ? 'denied' : 'off';
  const { publicKey } = await api.pushKey();
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await api.savePushSubscription(subscription.toJSON());
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  const subscription = await currentSubscription();
  if (subscription) {
    await api.deletePushSubscription(subscription.endpoint);
    await subscription.unsubscribe();
  }
  return 'off';
}

export async function testPush(): Promise<void> {
  const subscription = await currentSubscription();
  if (subscription) await api.testPush(subscription.endpoint);
}
